import { pageScrollBehavior } from "@/lib/scroll";
import { BrandLogo } from "@/components/ui/brand-logo";
import { NewsletterSignup } from "@/components/NewsletterSignup";
import { ArrowUp } from "lucide-react";
import { Link } from "react-router-dom";
import { useLocale } from "@/i18n/context";
import { localizedPath } from "@/i18n/routes";
import { hubListing, GUIDE_UI } from "@/content/guides/registry";
import { openCookieSettings } from "@/lib/consent";

export const Footer = () => {
  const { t, locale } = useLocale();
  const homePath = localizedPath("home", locale);

  // Content hub (guides) currently exists in sl + en only. Only surface the
  // column where there is content to link to.
  const hubPath = locale === "en" ? "/en/guide" : locale === "sl" ? "/vodnik" : null;
  const guides = hubPath ? hubListing(locale) : [];
  const guideHeading = GUIDE_UI[locale as "sl" | "en"]?.hubBreadcrumb;

  const scrollToTop = () => {
    if (typeof window !== "undefined") {
      window.scrollTo({ top: 0, behavior: pageScrollBehavior() });
    }
  };

  const legalLinks = [
    { key: "terms" as const, label: t.footer.terms },
    { key: "privacy" as const, label: t.footer.privacy },
    { key: "cookies" as const, label: t.footer.cookies },
    { key: "dataDeletion" as const, label: t.footer.dataDeletion },
  ];

  return (
    <footer className="relative border-t border-border/60 bg-[#EDF1E8]">
      <div className="relative container mx-auto px-4 py-14 sm:py-16">
        <NewsletterSignup className="mb-10 border-b border-border/60 pb-10" />

        <div className="grid gap-10 md:grid-cols-[1.2fr_2fr] md:items-start">
          <div className="space-y-4">
            <Link to={homePath} className="flex w-fit items-center gap-3" aria-label="nReport">
              <BrandLogo />
            </Link>
            <p className="max-w-sm text-sm leading-relaxed text-muted-foreground sm:text-base">
              {t.footer.tagline}
            </p>
          </div>

          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {hubPath && guides.length ? (
              <div>
                <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-foreground/70">
                  {guideHeading}
                </p>
                <ul className="space-y-2.5">
                  {guides.map((guide) => (
                    <li key={guide.path}>
                      <Link
                        to={guide.path}
                        className="text-sm text-muted-foreground transition-colors hover:text-primary"
                      >
                        {guide.title}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
            <div>
              <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-foreground/70">
                {t.footer.legalHeading}
              </p>
              <ul className="space-y-2.5">
                {legalLinks.map((link) => (
                  <li key={link.key}>
                    <Link
                      to={localizedPath(link.key, locale)}
                      className="text-sm text-muted-foreground transition-colors hover:text-primary"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
                <li>
                  {/* Withdrawing consent must be as easy as giving it
                      (GDPR Art. 7(3)), so the banner is reachable from
                      every page, not just the cookie policy. */}
                  <button
                    type="button"
                    onClick={openCookieSettings}
                    className="text-left text-sm text-muted-foreground transition-colors hover:text-primary"
                  >
                    {t.footer.cookieSettings}
                  </button>
                </li>
              </ul>
            </div>
            <div className="border-l-2 border-primary/20 pl-5">
              <p className="text-sm font-medium text-foreground">{t.footer.trustedSources}</p>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                {t.footer.trustedSourcesDesc}
              </p>
            </div>
          </div>
        </div>

        {/* Provider identification (ZEPT 6. člen / ZGD-1 45. člen) is not printed
            here — it lives in the opening section of the Terms and the Privacy
            Policy, both linked above from every page. */}
        <div className="mt-10 flex flex-col gap-3 border-t border-border/60 pt-6 text-sm text-muted-foreground sm:flex-row sm:items-center sm:justify-between">
          <p>{t.footer.copyright}</p>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-6">
            <p className="text-xs sm:text-sm">{t.footer.slogan}</p>
            <button
              type="button"
              onClick={scrollToTop}
              className="inline-flex items-center gap-1.5 text-xs font-medium transition-colors hover:text-primary sm:text-sm"
            >
              <ArrowUp className="h-4 w-4" />
              {t.footer.backToTop}
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
};
